import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Typography, Space, Form, Table, Button, Modal, message, Tooltip } from "antd";
import { EyeOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useStore } from "../../../root-store-context";
import Spinner from "../../../components/Spinner/Spiner";
import "antd/dist/reset.css";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import WorkOrderModal from "./WorkOrderModal";
import CorrigoConstants from "../../../Constants/CorrigoConstants";

const { Text } = Typography;

export default function WOList() {
  const { workOrderStore, applicationStore } = useStore();
  const navigate = useNavigate();

  const ASSET_ID = applicationStore?.SelectedAddress?.residentKey ?? 0;
  const USER_ID = applicationStore?.SelectedAddress?.userId ?? 0;
  const FIXED_CUSTOMER_ID = CorrigoConstants.FIXED_CUSTOMER_ID;

  const [loading, setLoading] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchWorkOrders = useCallback(
    async (assetId = ASSET_ID, userId = USER_ID, page = 1, pageSize = 10) => {
      // if (!assetId) {
      //   setWorkOrders([]);
      //   setPagination((p) => ({ ...p, total: 0 }));
      //   return;
      // }
      setLoading(true);
      try {
        const res = await workOrderStore.getWorkOrdersForResident(assetId, userId, page, pageSize);
        const entities = Array.isArray(res?.data?.Entities) ? res.data.Entities : [];
        const mapped = entities.map((wo) => ({
          WONumber: wo?.Data?.Number ?? null,
          WOSubType: wo?.Data?.SubType?.DisplayAs ?? null,
          Priority: wo?.Data?.Priority?.DisplayAs ?? null,
          Status: wo?.Data?.StatusId ?? null,
          AssignedTo: wo?.Data?.Employee?.DisplayAs ?? null,
          Customer: wo?.Data?.Customer?.DisplayAs ?? null,
          ContactName: wo?.Data?.ContactName ?? null,
          Description: wo?.Data?.TaskRefinement ?? null,
          CreatedDate: wo?.Data?.CreatedDateUtc ?? null,
          DueDate: wo?.Data?.DueDateUtc ?? null,
          AcknowledgeBy: wo?.Data?.AcknowledgeByUtc ?? null,
          Id: wo?.Data?.Id ?? null,
          EncryptedId: wo?.Data?.EncryptedId ?? null,
          raw: wo,
        }));
        const isLastPage = mapped.length < pageSize;
        const estimatedTotal = isLastPage ? (page - 1) * pageSize + mapped.length : (page + 1) * pageSize;
        setWorkOrders(mapped);
        setPagination({ current: page, pageSize, total: estimatedTotal });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("WOList: getWorkOrdersForResident error", err);
        message.error("Failed to load work orders");
        setWorkOrders([]);
        setPagination((p) => ({ ...p, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [workOrderStore, ASSET_ID, USER_ID]
  );

  useEffect(() => {
    fetchWorkOrders(ASSET_ID, USER_ID, pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWorkOrders, pagination.current, pagination.pageSize]);

  const handleTableChange = useCallback(
    ({ current = 1, pageSize = 10 }) => {
      setPagination((p) => ({ ...p, current, pageSize }));
      fetchWorkOrders(ASSET_ID, USER_ID, current, pageSize);
    },
    [fetchWorkOrders, ASSET_ID, USER_ID]
  );

  const handleOpenCreate = useCallback(() => setOpen(true), []);
  const openView = useCallback((workOrderId) => workOrderId ? navigate(`WODetails/${workOrderId}`) : message.error("Invalid work order id"), [navigate]);

  const handleCancelWO = useCallback(
    async (workOrderId) => {
      if (!workOrderId) return message.error("Invalid work order selected");
      setLoading(true);
      try {
        const { ok, notified, errorMessage } = await workOrderStore.cancelWorkOrder(workOrderId);
        if (ok) {
          message.success("Work order cancelled");
        } else if (!notified) {
          message.error(errorMessage || "Failed to cancel work order");
        }
        await fetchWorkOrders(ASSET_ID, USER_ID, pagination.current, pagination.pageSize);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("WOList: cancelWorkOrder error", err);
        message.error("Failed to cancel work order");
      } finally {
        setLoading(false);
      }
    },
    [fetchWorkOrders, pagination.current, pagination.pageSize, workOrderStore, ASSET_ID, USER_ID]
  );

  const showCancelConfirm = useCallback(
    (record) => {
      if (!record?.Id) return message.error("Invalid work order selected");
      Modal.confirm({
        title: "Cancel Work Order",
        centered: true,
        content: (
          <div>
            <p>
              Are you sure you want to cancel the work order <strong>{record.WONumber ?? record.Id}</strong>?
            </p>
          </div>
        ),
        okText: "Yes, Cancel",
        cancelText: "No",
        width: 520,
        okButtonProps: { style: { backgroundColor: "#5a7890", borderColor: "#5a7890", color: "white", fontWeight: 600 } },
        onOk: async () => handleCancelWO(record.Id),
      });
    },
    [handleCancelWO]
  );

  const handleSuccess = useCallback(() => {
    fetchWorkOrders(ASSET_ID, USER_ID, 1, pagination.pageSize);
    setPagination((p) => ({ ...p, current: 1 }));
    setOpen(false);
  }, [fetchWorkOrders, pagination.pageSize, ASSET_ID, USER_ID]);

  const columns = useMemo(
    () => [
      { title: "Id", dataIndex: "Id", key: "Id", width: 120 },
      { title: "WO Number", dataIndex: "WONumber", key: "WONumber", width: 140, render: (v) => <Text>{v ?? "-"}</Text> },
      { title: "Status", dataIndex: "Status", key: "Status", width: 140, render: (v) => <Text>{v ?? "-"}</Text> },
      { title: "Assigned To", dataIndex: "AssignedTo", key: "AssignedTo", width: 180, render: (v) => <Text>{v ?? "-"}</Text> },
      { title: "Created Date", dataIndex: "CreatedDate", key: "CreatedDate", width: 180, render: (t) => (t ? moment.utc(t).local().format("M/D/YYYY, hh:mm:ss A") : "-") },
      { title: "Due Date", dataIndex: "DueDate", key: "DueDate", width: 180, render: (t) => (t ? moment.utc(t).local().format("M/D/YYYY, hh:mm:ss A") : "-") },
      {
        title: "Actions",
        key: "actions",
        width: 120,
        align: "center",
        render: (_text, record) => {
          const isCancelled = record.Status === "Cancelled";
          return (
            <Space size="middle" data-row-action="true">
              <Tooltip title="View Work Order">
                <span data-row-action="true" onClick={(e) => { e.stopPropagation(); openView(record.EncryptedId); }} style={{ display: "inline-flex" }}>
                  <EyeOutlined style={{ color: "#5a7890", cursor: "pointer", fontSize: 20 }} aria-label={`view-workorder-${record.Id}`} />
                </span>
              </Tooltip>

              {isCancelled ? (
                <Tooltip title="Work Order is already cancelled">
                  <span data-row-action="true" onClick={(e) => e.stopPropagation()}>
                    <CloseCircleOutlined style={{ color: "#d9d9d9", cursor: "not-allowed", fontSize: 16 }} aria-hidden />
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Cancel Work Order">
                  <span data-row-action="true" onClick={(e) => { e.stopPropagation(); showCancelConfirm(record); }} role="button" tabIndex={0} style={{ display: "inline-flex" }}>
                    <CloseCircleOutlined style={{ color: "#5a7890", cursor: "pointer", fontSize: 16 }} aria-hidden />
                  </span>
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ],
    [openView, showCancelConfirm]
  );

  return (
    <>
      {loading && <Spinner />}

      <Form layout="inline" style={{ marginBottom: 16, justifyContent: "space-between", flexWrap: "wrap" }}>
        <Space style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          <h1>Maintenance Requests</h1>
        </Space>
        <Space style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Button type="primary" onClick={handleOpenCreate} aria-label="create-workorder">Create Work Order</Button>
        </Space>
      </Form>

      <Table
        columns={columns}
        dataSource={Array.isArray(workOrders) ? workOrders : []}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ~${total}`,
        }}
        onChange={handleTableChange}
        rowKey={(r) => r?.Id ?? String(r?.raw?.Data?.Id) ?? JSON.stringify(r)}
        scroll={{ x: 1200 }}
        onRow={(record) => ({
          onClick: (event) => {
            if (event.target.closest('[data-row-action="true"]')) return;
            openView(record.EncryptedId);
          },
          style: { cursor: "pointer" },
        })}
      />

      <WorkOrderModal
        customerId={FIXED_CUSTOMER_ID}
        open={open}
        onCancel={() => setOpen(false)}
        assetId={ASSET_ID}
        workOrderStore={workOrderStore}
        onSubmitSuccess={handleSuccess}
      />
    </>
  );
}
